import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, MapPin, Award, ArrowLeft, UtensilsCrossed, ShoppingBag, AlertTriangle } from 'lucide-react';
import { api, clearAuth, setStoredUser } from '@/lib/api';
import { toast } from 'sonner';
import { PublicHeader, Footer } from '@/components/layout/PublicHeader';
import { fmtMoney } from '@/lib/realtime';

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: user?.full_name || '', phone: user?.phone || '', birthday: user?.birthday || '' });
  const [saving, setSaving] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addr, setAddr] = useState({ label: 'Home', line1: '', line2: '', city: '', state: '', postal_code: '', notes: '' });
  // Delete-account flow state. We require the user to type DELETE and
  // re-enter their current password so the destructive action is both
  // intentional (no accidental taps on mobile) AND re-authenticated (an
  // attacker on a borrowed/idle session can't nuke the account).
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get('/me/addresses').then((r) => setAddresses(r.data || [])).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/me/profile', form);
      setUser(data);
      setStoredUser(data);
      toast.success('Profile updated');
    } catch (e) { toast.error('Failed to update'); } finally { setSaving(false); }
  };

  const addAddress = async () => {
    if (!addr.line1 || !addr.city) { toast.error('Street and city are required'); return; }
    try {
      const { data } = await api.post('/me/addresses', addr);
      setAddresses((p) => [...p, data]);
      setShowAdd(false);
      setAddr({ label: 'Home', line1: '', line2: '', city: '', state: '', postal_code: '', notes: '' });
      toast.success('Address saved');
    } catch (e) { toast.error('Failed'); }
  };
  const removeAddress = async (id) => {
    try { await api.delete(`/me/addresses/${id}`); setAddresses((p) => p.filter((a) => a.id !== id)); }
    catch (e) { toast.error('Failed'); }
  };

  const deleteAccount = async () => {
    if (deleteConfirm.trim() !== 'DELETE') {
      toast.error('Please type DELETE (uppercase) to confirm');
      return;
    }
    if (!deletePassword) {
      toast.error('Please enter your current password');
      return;
    }
    setDeleting(true);
    try {
      // Backend requires re-auth (current password) + confirm=DELETE.
      await api.delete('/me/account', { data: { password: deletePassword, confirm: 'DELETE' } });
      toast.success('Your account has been deleted');
      // logout() does a hard redirect to '/' itself, so we don't need nav().
      logout();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
      setShowDelete(false);
      setDeleteConfirm('');
      setDeletePassword('');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        {/* Breadcrumb / quick-nav so customers always have a clear way back
            to the menu or to their orders without hunting the user dropdown. */}
        <nav data-testid="profile-breadcrumb" className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <button
            data-testid="profile-back-home-button"
            onClick={() => nav('/')}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:text-primary"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Home
          </button>
          <span aria-hidden="true">/</span>
          <button
            data-testid="profile-back-menu-button"
            onClick={() => nav('/menu')}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:text-primary"
          >
            <UtensilsCrossed className="h-3.5 w-3.5" /> Menu
          </button>
          <span aria-hidden="true">/</span>
          <button
            data-testid="profile-back-orders-button"
            onClick={() => nav('/orders')}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:text-primary"
          >
            <ShoppingBag className="h-3.5 w-3.5" /> My Orders
          </button>
          <span aria-hidden="true">/</span>
          <span className="font-medium text-foreground">Profile</span>
        </nav>
        <h1 className="font-display text-2xl font-semibold">Profile</h1>
        <p className="text-sm text-muted-foreground">Your account info, rewards & saved addresses</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader><CardTitle>Account</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="profile-name-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="profile-phone-input" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Birthday</Label>
                <Input
                  data-testid="profile-birthday-input"
                  type="date"
                  value={form.birthday || ''}
                  onChange={(e) => setForm({ ...form, birthday: e.target.value })}
                  disabled={!!user?.birthday}
                  max={(() => {
                    const d = new Date();
                    d.setFullYear(d.getFullYear() - 15);
                    return d.toISOString().slice(0, 10);
                  })()}
                />
                <p className="text-[11px] text-muted-foreground">
                  {user?.birthday
                    ? 'Birthday is locked once set. Contact support to change it.'
                    : 'You must be at least 15 years old. Once saved, this cannot be changed.'}
                </p>
              </div>
              <Button data-testid="profile-save-button" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Loyalty</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">VIP level</p>
                  <p className="font-display text-2xl font-semibold">{user?.vip_level || 'Bronze'}</p>
                </div>
                <Badge variant="secondary" className="text-base"><Award className="mr-1 h-4 w-4" /> {user?.loyalty_points || 0} pts</Badge>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">Earn 1 point per {fmtMoney(1)}. Visit /loyalty to redeem 100 pts → {fmtMoney(5)} coupon.</p>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-4">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Saved addresses</CardTitle>
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
              <DialogTrigger asChild><Button data-testid="profile-add-address-button" size="sm"><Plus className="mr-1 h-4 w-4" /> Add address</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add address</DialogTitle><DialogDescription>Use saved addresses to check out faster.</DialogDescription></DialogHeader>
                <div className="grid gap-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Label</Label><Input data-testid="profile-address-label-input" value={addr.label} onChange={(e) => setAddr({ ...addr, label: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Postal code</Label><Input value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} /></div>
                  </div>
                  <div className="space-y-1.5"><Label>Street</Label><Input data-testid="profile-address-line1-input" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Apt / Suite</Label><Input value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>City</Label><Input data-testid="profile-address-city-input" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>State</Label><Input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} /></div>
                  </div>
                  <div className="space-y-1.5"><Label>Notes</Label><Textarea value={addr.notes} onChange={(e) => setAddr({ ...addr, notes: e.target.value })} placeholder="Gate code, drop-off instructions, etc." /></div>
                </div>
                <DialogFooter><Button data-testid="profile-address-submit-button" onClick={addAddress} disabled={!addr.line1 || !addr.city}>Save address</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {!addresses.length && <p className="text-sm text-muted-foreground">No saved addresses yet.</p>}
            <ul className="grid gap-2">
              {addresses.map((a) => (
                <li key={a.id} data-testid="profile-address-row" className="flex items-start justify-between gap-2 rounded border border-border bg-card p-3">
                  <div>
                    <p className="text-sm font-semibold"><MapPin className="mr-1 inline h-3.5 w-3.5 text-primary" /> {a.label || 'Address'}</p>
                    <p className="text-sm">{[a.line1, a.line2].filter(Boolean).join(', ')}</p>
                    <p className="text-xs text-muted-foreground">{[a.city, a.state, a.postal_code].filter(Boolean).join(', ')}</p>
                    {a.notes && <p className="mt-1 text-xs text-muted-foreground italic">&ldquo;{a.notes}&rdquo;</p>}
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removeAddress(a.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Danger zone — irreversible self-service account deletion.
            Customers only; staff accounts must be removed by an admin. */}
        <Card data-testid="profile-danger-zone" className="mt-6 border-rose-200 bg-rose-50/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" /> Danger zone
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-rose-900/80">
              <p className="font-medium text-rose-900">Delete your account</p>
              <p>This permanently removes your profile from City Grill. Past orders stay on file for accounting, but your personal info is wiped. This cannot be undone.</p>
            </div>
            <Dialog open={showDelete} onOpenChange={(o) => { setShowDelete(o); if (!o) { setDeleteConfirm(''); setDeletePassword(''); } }}>
              <DialogTrigger asChild>
                <Button
                  data-testid="profile-delete-account-button"
                  variant="destructive"
                  className="shrink-0"
                >
                  <Trash2 className="mr-1.5 h-4 w-4" /> Delete account
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="profile-delete-account-dialog">
                <DialogHeader>
                  <DialogTitle className="text-rose-700">Delete your City Grill account?</DialogTitle>
                  <DialogDescription>
                    This action is permanent. Your profile, saved addresses, and loyalty points will be removed and you will be signed out immediately.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  <Label htmlFor="confirm-delete-input">Type <span className="font-mono font-semibold text-rose-700">DELETE</span> to confirm</Label>
                  <Input
                    id="confirm-delete-input"
                    data-testid="profile-delete-confirm-input"
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder="DELETE"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-delete-password">Re-enter your current password</Label>
                  <Input
                    id="confirm-delete-password"
                    data-testid="profile-delete-password-input"
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Current password"
                    autoComplete="current-password"
                  />
                  <p className="text-xs text-muted-foreground">We ask for your password to prevent someone else who is logged into this device from deleting your account.</p>
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button
                    data-testid="profile-delete-cancel-button"
                    variant="ghost"
                    onClick={() => { setShowDelete(false); setDeleteConfirm(''); setDeletePassword(''); }}
                    disabled={deleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="profile-delete-confirm-button"
                    variant="destructive"
                    onClick={deleteAccount}
                    disabled={deleting || deleteConfirm.trim() !== 'DELETE' || !deletePassword}
                  >
                    {deleting ? 'Deleting…' : 'Permanently delete'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
